import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import pagesAPI from '../../api/pages';
import './pages.css';

const CreatePageModal = ({ show, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    pageName: '',
    pageUrl: '',
    pageType: 'landing',
    status: 'draft',
  });
  const [reservedRoutes, setReservedRoutes] = useState(null);
  const [urlValidation, setUrlValidation] = useState({ isValid: true, message: '' });
  const [showReservedRoutes, setShowReservedRoutes] = useState(false);

  // Fetch reserved routes when modal opens
  useEffect(() => {
    if (show && !reservedRoutes) {
      fetchReservedRoutes();
    }
  }, [show, reservedRoutes]);

  const fetchReservedRoutes = async () => {
    try {
      const response = await pagesAPI.getReservedRoutes();
      if (response?.data?.status === 200) {
        setReservedRoutes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching reserved routes:', error);
    }
  };

  // Validate URL against reserved routes
  const validateUrl = (url) => {
    if (!url || !url.trim()) {
      return { isValid: true, message: '' };
    }

    const normalizedUrl = url.trim().toLowerCase();

    // Check if URL starts with /
    if (!normalizedUrl.startsWith('/')) {
      return { isValid: false, message: 'URL must start with /' };
    }

    // Check invalid characters
    const invalidChars = /[<>:"\\|?*#]/;
    if (invalidChars.test(normalizedUrl)) {
      return { isValid: false, message: 'URL contains invalid characters. Allowed: a-z, 0-9, -, _, /' };
    }

    if (!reservedRoutes) {
      return { isValid: true, message: '', suggestion: '' };
    }

    // Remove trailing slash for comparison (except root)
    const urlForCheck = normalizedUrl === '/' ? normalizedUrl : normalizedUrl.replace(/\/$/, '');

    // Check static routes (exact match only)
    if (reservedRoutes.staticRoutes?.includes(urlForCheck)) {
      // Suggest alternative
      const suggestion = `${urlForCheck}-page`;
      return {
        isValid: false,
        message: `This URL is reserved (exact match: ${urlForCheck})`,
        suggestion: `Try: ${suggestion}`
      };
    }

    // Check prefixes (only if it STARTS with the prefix)
    for (const prefix of reservedRoutes.prefixes || []) {
      if (urlForCheck === prefix || urlForCheck.startsWith(`${prefix}/`)) {
        const suggestion = `/custom${urlForCheck}`;
        return {
          isValid: false,
          message: `URLs starting with ${prefix}/ are reserved for system routes`,
          suggestion: `Try: ${suggestion}`
        };
      }
    }

    // No need to check dynamic patterns on frontend
    // Backend will handle exact conflicts with actual dynamic routes
    // Frontend should only block prefixes and exact static matches

    return { isValid: true, message: '', suggestion: '' };
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Auto-generate URL from page name
    if (field === 'pageName' && !formData.pageUrl) {
      const autoUrl = '/' + value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData((prev) => ({
        ...prev,
        pageUrl: autoUrl,
      }));

      // Validate auto-generated URL
      const validation = validateUrl(autoUrl);
      setUrlValidation(validation);
    }

    // Validate URL in real-time
    if (field === 'pageUrl') {
      const validation = validateUrl(value);
      setUrlValidation(validation);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.pageName.trim()) {
      toast.error('Please enter a page name');
      return;
    }

    if (!formData.pageUrl.trim()) {
      toast.error('Please enter a page URL');
      return;
    }

    if (!formData.pageUrl.startsWith('/')) {
      toast.error('Page URL must start with /');
      return;
    }

    // Check URL validation
    const validation = validateUrl(formData.pageUrl);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    try {
      setLoading(true);
      const response = await pagesAPI.createPage(formData);

      if (response?.data?.status === 200 || response?.data?.success) {
        const newPageId = response.data.data._id;
        toast.success('Page created successfully!');
        onClose();
        // Navigate to edit page
        navigate(`/pages/${newPageId}/edit`);
      }
    } catch (error) {
      console.error('Error creating page:', error);
      const errorMessage = error?.message || 'Failed to create page';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        pageName: '',
        pageUrl: '',
        pageType: 'landing',
        status: 'draft',
      });
      setUrlValidation({ isValid: true, message: '' });
      onClose();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New Page</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">
              Page Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={formData.pageName}
              onChange={(e) => handleChange('pageName', e.target.value)}
              placeholder="e.g., Weight Loss Landing Page"
              required
              autoFocus
            />
            <small className="form-text text-muted">
              A descriptive name for your page
            </small>
          </div>

          <div className="mb-3">
            <label className="form-label">
              Page URL <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${!urlValidation.isValid ? 'is-invalid' : urlValidation.message === '' && formData.pageUrl ? 'is-valid' : ''}`}
              value={formData.pageUrl}
              onChange={(e) => handleChange('pageUrl', e.target.value)}
              placeholder="/weight-loss"
              required
            />
            {!urlValidation.isValid && (
              <>
                <div className="invalid-feedback d-block">
                  <i className="fa fa-exclamation-triangle me-1"></i>
                  {urlValidation.message}
                </div>
                {urlValidation.suggestion && (
                  <div className="text-primary mt-1" style={{ fontSize: '0.875rem' }}>
                    <i className="fa fa-lightbulb me-1"></i>
                    <strong>Suggestion:</strong> {urlValidation.suggestion}
                  </div>
                )}
              </>
            )}
            {urlValidation.isValid && formData.pageUrl && (
              <div className="valid-feedback d-block">
                <i className="fa fa-check-circle me-1"></i>
                URL is available
              </div>
            )}
            <small className="form-text text-muted d-block mt-1">
              The URL path where this page will be accessible (must start with /)
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-2"
                onClick={() => setShowReservedRoutes(!showReservedRoutes)}
              >
                <i className={`fa fa-${showReservedRoutes ? 'eye-slash' : 'eye'} me-1`}></i>
                {showReservedRoutes ? 'Hide' : 'View'} Reserved URLs
              </button>
            </small>

            {/* Reserved Routes Info */}
            {showReservedRoutes && reservedRoutes && (
              <div className="alert alert-warning mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
                <div className="fw-bold mb-2">
                  <i className="fa fa-exclamation-circle me-1"></i>
                  Reserved URLs - What You Cannot Use
                </div>

                <div className="mb-2">
                  <strong>🚫 Protected Prefixes (Block entire sections):</strong>
                  <div className="ms-3 mt-1">
                    {reservedRoutes.prefixes?.map((prefix, idx) => (
                      <div key={idx} className="mb-1">
                        <code className="me-2">{prefix}/{'\u002A'}</code>
                        <small className="text-muted">- Blocks {prefix}/ and anything starting with {prefix}/</small>
                      </div>
                    ))}
                  </div>
                  <small className="text-muted ms-3">Example: ❌ /api/custom, ❌ /dashboard/mypage</small>
                </div>

                <div className="mb-2">
                  <strong>🚫 Exact URL Matches (Specific pages only):</strong>
                  <div className="ms-3 mt-1">
                    <code className="me-2 d-inline-block mb-1">/login</code>
                    <code className="me-2 d-inline-block mb-1">/register</code>
                    <code className="me-2 d-inline-block mb-1">/dashboard</code>
                    <code className="me-2 d-inline-block mb-1">/checkout</code>
                    <code className="me-2 d-inline-block mb-1">/products</code>
                    <code className="me-2 d-inline-block mb-1">/blog</code>
                    <code className="me-2 d-inline-block mb-1">/contact</code>
                    <span className="text-muted">...and {(reservedRoutes.staticRoutes?.length || 0) - 7} more</span>
                  </div>
                  <small className="text-muted ms-3">Example: ❌ /login but ✅ /login-page is OK</small>
                </div>

                <div className="mb-2">
                  <strong>✅ What IS Allowed:</strong>
                  <div className="ms-3 mt-1">
                    <div><code>/category/weight-loss</code> <span className="text-success">✓ Allowed</span></div>
                    <div><code>/blog/my-article</code> <span className="text-success">✓ Allowed</span></div>
                    <div><code>/onlineclinic/custom</code> <span className="text-success">✓ Allowed</span></div>
                    <div><code>/landing/summer-sale</code> <span className="text-success">✓ Allowed</span></div>
                    <div><code>/campaigns/2024</code> <span className="text-success">✓ Allowed</span></div>
                  </div>
                  <small className="text-muted ms-3">
                    Dynamic routes (/blog/:slug) are NOT blocked - they work alongside CMS pages
                  </small>
                </div>

                <div className="mt-2 pt-2 border-top">
                  <small className="text-muted">
                    <i className="fa fa-info-circle me-1"></i>
                    <strong>How it works:</strong> Only exact matches and protected prefixes are blocked.
                    URLs like /blog/article coexist with dynamic /blog/:slug routes.
                  </small>
                </div>
              </div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">Page Type</label>
            <select
              className="form-select"
              value={formData.pageType}
              onChange={(e) => handleChange('pageType', e.target.value)}
            >
              <option value="landing">Landing Page</option>
              <option value="category">Category Page</option>
              <option value="product">Product Page</option>
              <option value="simple">Simple Page</option>
            </select>
            <small className="form-text text-muted">
              The type of page you&apos;re creating
            </small>
          </div>

          <div className="mb-3">
            <label className="form-label">Initial Status</label>
            <select
              className="form-select"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <option value="draft">Draft (not visible)</option>
              <option value="published">Published (visible)</option>
            </select>
            <small className="form-text text-muted">
              You can change this later
            </small>
          </div>

          <div className="alert alert-info mb-0">
            <i className="fa fa-info-circle me-2"></i>
            <strong>Note:</strong> After creating the page, you&apos;ll be taken to the page builder where you can add sections and content.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !urlValidation.isValid}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Creating...
              </>
            ) : (
              <>
                <i className="fa fa-plus me-2"></i>
                Create Page
              </>
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default CreatePageModal;
